import React from 'react'
import styled, {css} from 'styled-components'
import {AlertTriangle, CheckCircle, Info, X as CloseIcon, XCircle} from 'lucide-react'

/**
 * Alert — inline status banner / notice strip.
 *
 * The DS-sanctioned replacement for ad-hoc banners (the old
 * `BackupQuotaBanner`, `CandidateMatchBanner`, and MUI's `<Alert>`).
 * Uses the same status-family tokens as StatusPill (`--*-100` bg,
 * `--*-500` border + icon, `--*-800` text) so a11y is baked in.
 *
 * Use for:
 *   - Persistent inline notices that explain page state
 *     ("3 of 8 candidates assigned — 5 still needed")
 *   - Form-level errors that aren't tied to a single field
 *   - Heads-up notices about a feature ("File over 10MB — compressing…")
 *
 * Use Toast for transient confirmations instead. Use StatusPill for a
 * compact label on a row/card.
 *
 * @example minimal
 *   <Alert tone="error">Couldn't upload file. Check the format and try again.</Alert>
 *
 * @example rich
 *   <Alert
 *     tone="success"
 *     title="All assignments complete"
 *     action={<Button variant="ghost" size="sm">Submit</Button>}
 *     onDismiss={() => setShown(false)}>
 *     8 of 8 backup candidates assigned across 2 groups.
 *   </Alert>
 */

/* ====================================================================
   TYPES
   ==================================================================== */

export type AlertTone = 'success' | 'warning' | 'error' | 'info'

export interface AlertProps {
  /** Status tone — drives bg, border, icon, text color. Defaults to `info`. */
  tone?: AlertTone
  /** Optional bold title rendered above the body. */
  title?: string
  /** Body content. */
  children?: React.ReactNode
  /**
   * Override the leading icon. Pass `false` to hide it entirely.
   * Defaults to the tone-appropriate lucide icon.
   */
  icon?: React.ReactNode | false
  /** Optional right-aligned action slot (button / link). */
  action?: React.ReactNode
  /** When provided, renders a close (X) button on the right. */
  onDismiss?: () => void
  className?: string
  'data-testid'?: string
}

/* ====================================================================
   TOKENS
   ==================================================================== */

const TONE_STYLES: Record<AlertTone, {bg: string; border: string; icon: string; text: string}> = {
  success: {
    bg: 'var(--success-100)',
    border: 'var(--success-500)',
    icon: 'var(--success-500)',
    text: 'var(--success-800)',
  },
  warning: {
    bg: 'var(--warning-100)',
    border: 'var(--warning-500)',
    icon: 'var(--warning-500)',
    text: 'var(--warning-800)',
  },
  error: {
    bg: 'var(--error-100)',
    border: 'var(--error-500)',
    icon: 'var(--error-500)',
    text: 'var(--error-800)',
  },
  info: {
    bg: 'var(--info-100)',
    border: 'var(--info-500)',
    icon: 'var(--info-500)',
    text: 'var(--info-800)',
  },
}

const DEFAULT_ICONS: Record<AlertTone, React.ComponentType<{size?: number}>> = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
}

/** error/warning need assertive (screen reader interrupts);
 * success/info use polite via role="status". */
const TONE_ROLE: Record<AlertTone, 'alert' | 'status'> = {
  success: 'status',
  warning: 'alert',
  error: 'alert',
  info: 'status',
}

/* ====================================================================
   STYLED
   ==================================================================== */

const toneCss = ($tone: AlertTone) => {
  const t = TONE_STYLES[$tone]
  return css`
    background: ${t.bg};
    border: 1px solid ${t.border};
    color: ${t.text};
  `
}

const Root = styled.div<{$tone: AlertTone}>`
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-sm);
  font-size: var(--fs-body);
  line-height: var(--lh-normal);
  ${({$tone}) => toneCss($tone)}
`

const IconSlot = styled.span<{$tone: AlertTone}>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  /* Nudge icon to align with the first line of text. */
  margin-top: 2px;
  color: ${({$tone}) => TONE_STYLES[$tone].icon};
`

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
`

const Title = styled.div`
  font-weight: var(--fw-semibold);
  /* Title inherits color from Root (the tone's --*-800). */
`

const ActionSlot = styled.div`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
`

const DismissButton = styled.button<{$tone: AlertTone}>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  margin: -2px -4px -2px 0;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: ${({$tone}) => TONE_STYLES[$tone].text};
  cursor: pointer;
  opacity: 0.7;
  transition: opacity var(--motion-fast) var(--motion-ease),
    background var(--motion-fast) var(--motion-ease);

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.05);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
    opacity: 1;
  }
`

/* ====================================================================
   COMPONENT
   ==================================================================== */

export const Alert: React.FC<AlertProps> = ({
  tone = 'info',
  title,
  children,
  icon,
  action,
  onDismiss,
  className,
  'data-testid': dataTestId,
}) => {
  const role = TONE_ROLE[tone]

  let renderedIcon: React.ReactNode = null
  if (icon === false) {
    renderedIcon = null
  } else if (icon !== undefined) {
    renderedIcon = icon
  } else {
    const IconComponent = DEFAULT_ICONS[tone]
    renderedIcon = <IconComponent size={16} />
  }

  return (
    <Root $tone={tone} role={role} className={className} data-testid={dataTestId} data-tone={tone}>
      {renderedIcon !== null && <IconSlot $tone={tone}>{renderedIcon}</IconSlot>}
      <Body>
        {title && <Title>{title}</Title>}
        {children && <div>{children}</div>}
      </Body>
      {action && <ActionSlot>{action}</ActionSlot>}
      {onDismiss && (
        <DismissButton
          $tone={tone}
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          data-testid={dataTestId ? `${dataTestId}-dismiss` : undefined}>
          <CloseIcon size={14} />
        </DismissButton>
      )}
    </Root>
  )
}

export default Alert
